<template>
  <header id="tb" class="site-toolbar nontransparent">
    <div class="toolbar-inner">
      <div id="toolbartitle" class="nontransparent">
        <router-link to="/">
          <img
            src="../assets/images/logos/2025/PGPTitle_Logo_Color_LightBG (horizontal).png"
            alt="PGPTitle Logo"
            class="img-fluid"
            style="max-height: 50px"
          />
        </router-link>
      </div>
      <nav class="header-nav d-none d-lg-flex push-right">
        <router-link
          v-for="item in menu"
          :key="item.title"
          :to="item.link"
          class="header-link nontransparent"
        >
          {{ item.title }}
        </router-link>
      </nav>
      <div class="d-lg-none push-right">
        <button
          ref="menuToggleButton"
          class="menu-toggle"
          type="button"
          aria-label="Toggle navigation menu"
          @click="menuOpen = !menuOpen"
        >
          <span class="hamburger-icon" aria-hidden="true"></span>
        </button>
      </div>
    </div>
    <div v-if="menuOpen" ref="mobileMenu" class="mobile-menu d-lg-none">
      <router-link
        v-for="item in menu"
        :key="`mobile-${item.title}`"
        :to="item.link"
        class="menulinks"
        @click="menuOpen = false"
      >
        {{ item.title }}
      </router-link>
    </div>
  </header>
</template>

<style>
a.menulinks:hover {
  color: #018181;
  text-decoration: underline;
}

a.menulinks {
  display: block;
  text-decoration-line: none;
  color: #003c5b;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.25;
  padding: 12px 18px;
  font-family: Source Sans Pro, Arial, Helvetica, sans-serif;
}
.site-toolbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: auto;
  z-index: 2000;
  background: #fff;
  box-sizing: border-box;
  overflow-x: hidden;
}
.toolbar-inner {
  width: 100%;
  min-height: 100px;
  padding: 7px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  min-width: 0;
}
.header-nav {
  height: 100%;
  align-items: stretch;
  min-width: 0;
}
.push-right {
  margin-left: auto;
}
.header-link {
  height: 100%;
  display: inline-flex;
  align-items: center;
  font-weight: 600;
  text-transform: none;
  font-size: 20px;
  font-family: Source Sans Pro, Arial, Helvetica, sans-serif;
  text-decoration: none;
  padding: 0 0.75rem;
  position: relative;
  overflow: hidden;
  border-radius: 0;
}
.header-link::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  opacity: 0;
  background: radial-gradient(
      circle,
      rgb(255 255 255 / 0.35) 12%,
      transparent 12.01%
    )
    center / 1000% 1000% no-repeat;
  transition: opacity 0.45s ease, background-size 0.45s ease;
}
.header-link:active::after {
  opacity: 1;
  background-size: 100% 100%;
  transition: opacity 0s, background-size 0s;
}
.menu-toggle {
  border: none;
  background: #f5f5f5;
  color: #003c5b;
  border-radius: 50%;
  padding: 0;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.menu-toggle::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 50%;
  pointer-events: none;
  opacity: 0;
  background: radial-gradient(
      circle,
      rgb(0 60 91 / 0.2) 10%,
      transparent 10.01%
    )
    center / 1000% 1000% no-repeat;
  transition: opacity 0.5s ease, background-size 0.5s ease;
}

.menu-toggle:active::after {
  opacity: 1;
  background-size: 100% 100%;
  transition: opacity 0s, background-size 0s;
}

.hamburger-icon {
  position: relative;
  width: 18px;
  height: 2px;
  background-color: #003c5b;
  display: inline-block;
}

.hamburger-icon::before,
.hamburger-icon::after {
  content: "";
  position: absolute;
  left: 0;
  width: 18px;
  height: 2px;
  background-color: #003c5b;
}

.hamburger-icon::before {
  top: -6px;
}

.hamburger-icon::after {
  top: 6px;
}
.mobile-menu {
  position: fixed;
  top: 64px;
  right: 12px;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 8px 18px rgb(0 0 0 / 0.18);
  padding: 8px 0;
  z-index: 2500;
}

.mobile-menu a.menulinks {
  text-align: left;
}

@media only screen and (max-width: 959px) {
  .mobile-menu {
    top: 56px;
  }
}

@media only screen and (max-width: 600px) {
  a.menulinks {
    font-size: 18px;
    padding: 10px 14px;
  }
  .mobile-menu {
    width: min(320px, calc(100vw - 16px));
    right: 8px;
  }
}
@media only screen and (max-width: 959px) {
  .toolbar-inner {
    min-height: 60px;
  }
}
</style>

<script setup>
import { onBeforeUnmount, onMounted, ref } from "vue";
import { eventBus } from "../eventBus";

const menuOpen = ref(false);
const mobileMenu = ref(null);
const menuToggleButton = ref(null);
const currentRouteName = ref("Home");
const lastScrollY = ref(0);
const menu = [
  { title: "Company Information", link: "/CompanyInformation" },
  { title: "Leadership", link: "/Leadership" },
  { title: "Contact Us", link: "/ContactUs" },
  { title: "Privacy Policy", link: "/PrivacyPolicy" },
];

const setTransparent = () => {
  const elements = document.getElementsByClassName("nontransparent");
  for (let i = elements.length - 1; i >= 0; --i) {
    elements[i].classList.add("transparent");
    elements[i].classList.remove("nontransparent");
  }
};

const setOpaque = () => {
  const elements = document.getElementsByClassName("transparent");
  for (let i = elements.length - 1; i >= 0; --i) {
    elements[i].classList.add("nontransparent");
    elements[i].classList.remove("transparent");
  }
};

const shouldForceOpaque = () => window.innerWidth < 992;

const applyHeaderState = (routeName, scrollY) => {
  if (routeName !== "Home" || shouldForceOpaque() || scrollY > 100) {
    setOpaque();
  } else {
    setTransparent();
  }
};

const changeHeaderTransparencyHandler = (msg) => {
  lastScrollY.value = Number(msg) || 0;
  applyHeaderState(currentRouteName.value, lastScrollY.value);
};

const routeChangedHandler = (msg) => {
  currentRouteName.value = msg?.name || "";
  applyHeaderState(currentRouteName.value, lastScrollY.value);
};

const pageResponseHandler = (msg) => {
  currentRouteName.value = msg?.name || "";
  applyHeaderState(currentRouteName.value, lastScrollY.value);
};

const handleResize = () => {
  applyHeaderState(currentRouteName.value, lastScrollY.value);
};

const handleDocumentClick = (event) => {
  if (!menuOpen.value) {
    return;
  }

  const menuEl = mobileMenu.value;
  const toggleEl = menuToggleButton.value;
  const target = event.target;

  if (
    (menuEl && menuEl.contains(target)) ||
    (toggleEl && toggleEl.contains(target))
  ) {
    return;
  }

  menuOpen.value = false;
};

onMounted(() => {
  lastScrollY.value = window.scrollY || window.pageYOffset || 0;
  eventBus.$on("changeHeaderTransparency", changeHeaderTransparencyHandler);
  eventBus.$on("routeChangedEvent", routeChangedHandler);
  eventBus.$on("pageResponse", pageResponseHandler);
  eventBus.$emit("pageRequest");
  document.addEventListener("click", handleDocumentClick);
  window.addEventListener("resize", handleResize);
});

onBeforeUnmount(() => {
  eventBus.$off("changeHeaderTransparency", changeHeaderTransparencyHandler);
  eventBus.$off("routeChangedEvent", routeChangedHandler);
  eventBus.$off("pageResponse", pageResponseHandler);
  document.removeEventListener("click", handleDocumentClick);
  window.removeEventListener("resize", handleResize);
});
</script>
